import React, { useState, useEffect, useMemo } from 'react';
import { getAuth, signOut } from 'firebase/auth';
import { useNavigate, Link } from 'react-router-dom';
import { collection, query, getDocs, where, orderBy, limit, doc, updateDoc, Timestamp, serverTimestamp, addDoc } from 'firebase/firestore';
import { db } from '../../firebase';
import LightModeLoanCard from './LightModeLoanCard';
import { BarsArrowUpIcon, ChevronDownIcon, ChevronLeftIcon, ChevronRightIcon, MagnifyingGlassIcon, FunnelIcon, StarIcon } from '@heroicons/react/20/solid';
import { BankManagement } from './BankManagement';
import { PreLenderCard, InTalksCard, ClosedDealCard, PrequalificationCard } from './LoanCards';
import { LoanStatuses } from '../constants/statuses';
import { format } from 'date-fns';
import { Menu } from '@headlessui/react';
import { EllipsisVerticalIcon } from '@heroicons/react/20/solid';
import { ExclamationTriangleIcon } from '@heroicons/react/20/solid';
import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/20/solid';
import { PaperClipIcon } from '@heroicons/react/20/solid';
import LenderMatchCard from './LenderMatchCard';
import CreateLenderMatchModal from './CreateLenderMatchModal';
import PrequalificationTabDetails from './PrequalificationTabDetails';
import { ClockIcon, PlusIcon, XMarkIcon, ArrowTopRightOnSquareIcon, BellIcon, UserPlusIcon, DocumentTextIcon, PhoneIcon, GlobeAltIcon, EnvelopeIcon } from '@heroicons/react/20/solid';
import { FireIcon, ClockIcon as ClockOutlineIcon, CheckCircleIcon as CheckCircleOutlineIcon, ArrowRightOnRectangleIcon } from '@heroicons/react/24/outline';
import OffMarketModal from './OffMarketModal';
import FollowUpEmailModal from './FollowUpEmailModal';
import PendingSearcherModal from './PendingSearcherModal';
import BrokersTab from './BrokersTab';
import UploadBrokerPrequalificationModal from './UploadBrokerPrequalificationModal';
import NewListingLauncherModal from './NewListingLauncherModal';
import DailyTodo from './DailyTodo';
import AdminOverview from './AdminOverview';
import SalesCommissionHub from './SalesCommissionHub';


// Define separate tabs for each loan type
const acquisitionTabs = [
  { name: 'Recent Activity', section: 'recentActivity' },
  { name: 'Needs Attention', section: 'needsAttention' },
  { name: 'Files Ready', section: 'filesReady' },
  { name: 'Underwriting', section: 'underwriting' },
];

const workingCapitalTabs = [
  { name: 'Recent Activity', section: 'recentActivity' },
  { name: 'New Applications', section: LoanStatuses.NEW_APPLICATION },
  { name: 'Submitted', section: LoanStatuses.SUBMITTED },
  { name: 'Prequalified', section: LoanStatuses.PREQUALIFIED_FOR },
  { name: 'Document Review', section: LoanStatuses.DOCUMENT_CLARIFICATION },
  { name: 'Underwriting', section: LoanStatuses.UNDERWRITING },
  { name: 'Funded', section: LoanStatuses.FUNDED },
  { name: 'Bank Management', section: 'bankManagement' },
];

const loanPurposeFilters = [
  { value: 'all', label: 'All Purposes' },
  { value: 'Acquisition', label: 'Acquisition' },
];

const LOAN_TYPE_FILTERS = [
  { value: 'all', label: 'All Types' },
  { value: 'Acquisition', label: 'Acquisition' },
];

const callsTabs = [
  { name: 'Upcoming Calls', section: 'upcomingCalls' },
  { name: 'Scheduled', section: 'scheduledCalls' },
];

// Add this with other tab definitions
const lenderMatchTabs = [
  { name: 'Pending Matches', section: 'pendingMatches' },
  { name: 'Matched', section: 'matched' },
  { name: 'Declined', section: 'declined' },
];

// Add this constant near the top with other constants
const LOAN_STAGES = [
  { id: 'underwriting', label: 'Underwriting', color: 'purple' },
  { id: 'prequalified', label: 'Prequalified', color: 'orange' },
  { id: 'documentReview', label: 'Document Review', color: 'orange' },
  { id: 'awaitingDocs', label: 'Awaiting Docs', color: 'blue' },
  { id: 'docsUploaded', label: 'Docs Ready', color: 'yellow' },
  { id: 'funded', label: 'Funded', color: 'green' },
];

// *** Updated Status Checklists Definition ***
const statusChecklists = {
  [LoanStatuses.NEW_APPLICATION]: [
    'Call to explain process',
  ],
  [LoanStatuses.SUBMITTED]: [ // Checklist items for when the status *is* SUBMITTED
    'Send initial package to bank'
  ],
  // Removed SUBMITTED_DocsUploaded key
  [LoanStatuses.PREQUALIFIED_FOR]: [
    'Send consulting agreement',
    'Signed consulting agreement',
  ],
  [LoanStatuses.TERM_SHEET_EXTENDED]: [
    'Send consulting agreement',
    'Signed consulting agreement',
  ],
  [LoanStatuses.DOCUMENT_CLARIFICATION]: [
    'Review received documents',
    'Send updated package to bank',
  ],
  [LoanStatuses.CLOSING]: [
    'Send closing package to bank',
  ],
   // Add other statuses if they need checklists
   [LoanStatuses.UNDERWRITING]: [], // Example: No checklist needed
   [LoanStatuses.REJECTED]: [],
   [LoanStatuses.FUNDED]: [],
};
// *** End Status Checklists Definition ***

function classNames(...classes) {
  return classes.filter(Boolean).join(' ')
}

// Update the status mapping
const statuses = {
  'New Application': 'text-blue-700 bg-blue-50 ring-blue-600/20',
  'Submitted': 'text-yellow-700 bg-yellow-50 ring-yellow-600/20',
  'Funded': 'text-green-700 bg-green-50 ring-green-600/20',
  'Pre-lender': 'text-purple-700 bg-purple-50 ring-purple-600/20',
  'Document Review': 'text-orange-700 bg-orange-50 ring-orange-600/20',
  'Prequalified': 'text-orange-700 bg-orange-50 ring-orange-600/20',
  'Acquisition': 'text-emerald-700 bg-emerald-50 ring-emerald-600/20',  // Add this line
  'Underwriting': 'text-purple-700 bg-purple-50 ring-purple-600/20',
};

const getDaysSince = (date) => {
  if (!date) return 0;
  // Ensure Firestore Timestamp or JS Date object
  const targetDate = date?.toDate ? date.toDate() : new Date(date);
  if (isNaN(targetDate.getTime())) {
   // console.warn("Invalid date passed to getDaysSince:", date);
    return 0; // Return 0 if the date is invalid
  }
  const today = new Date();
  const diffTime = Math.abs(today - targetDate);
  return Math.ceil(diffTime / (1000 * 60 * 60 * 24));
};

// Add this near the top with other constants
const alphabet = '#ABCDEFGHIJKLMNOPQRSTUVWXYZ'.split('');

// Define getFirstChar *before* it's used in renderSection
const getFirstChar = (loan) => {
  const name = loan.businessName || `${loan.firstName} ${loan.lastName}`.trim();
  const char = name.charAt(0).toUpperCase();
  return /[A-Z]/.test(char) ? char : '#';
};

const AdminDashboard = () => {
  const navigate = useNavigate();
  const [recentUsers, setRecentUsers] = useState([]);
  const [preLenderLoans, setPreLenderLoans] = useState([]);
  const [inTalksLoans, setInTalksLoans] = useState([]);
  const [closedDeals, setClosedDeals] = useState([]);
  const [prequalifications, setPrequalifications] = useState([]);
  const [borrowerPrequalifications, setBorrowerPrequalifications] = useState([]);
  const [activeSection, setActiveSection] = useState('recentActivity');
  const [searchTerm, setSearchTerm] = useState('');
  const [selectedLoanType, setSelectedLoanType] = useState('adminHome');
  const [loans, setLoans] = useState([]);
  const [selectedDate, setSelectedDate] = useState(new Date());
  const [searchResults, setSearchResults] = useState([]);
  const [isSearching, setIsSearching] = useState(false);
  const [displayStats, setDisplayStats] = useState(null);
  const [statusFilter, setStatusFilter] = useState(null);
  const [lenderMatches, setLenderMatches] = useState([]);
  const [isCreateModalOpen, setIsCreateModalOpen] = useState(false);
  const [selectedStage, setSelectedStage] = useState(null);
  const [notifications, setNotifications] = useState([]);
  const [dismissedNotifications, setDismissedNotifications] = useState(() => {
    // Load dismissed notifications from localStorage on initial load
    const saved = localStorage.getItem('dismissedNotifications');
    return saved ? new Set(JSON.parse(saved)) : new Set();
  });
  
  // New Follow Up Modal state
  const [isNewFollowUpModalOpen, setIsNewFollowUpModalOpen] = useState(false);
  const [newFollowUpData, setNewFollowUpData] = useState({
    personName: '',
    email: '',
    phoneNumber: '',
    followUpDate: '',
    reason: ''
  });
  const [followUpRefreshTrigger, setFollowUpRefreshTrigger] = useState(0);

  // Off-Market Searchers state
  const [offMarketSearchers, setOffMarketSearchers] = useState([]);
  const [showOffMarketModal, setShowOffMarketModal] = useState(false);
  const [showPendingSearcherModal, setShowPendingSearcherModal] = useState(false);
  const [showEmailModal, setShowEmailModal] = useState(false);
  const [showUploadPrequalModal, setShowUploadPrequalModal] = useState(false);
  const [showNewListingLauncher, setShowNewListingLauncher] = useState(false);
  const [emailModalData, setEmailModalData] = useState({
    recipientEmail: '',
    recipientName: '',
    templateType: 'offMarketFollowUp',
    businessName: ''
  });

  // Update the isToday function to handle UTC dates
  const isToday = (date1, date2) => {
    if (!date1 || !date2) return false;
    
    // Ensure we're working with Date objects
    const d1 = date1 instanceof Date ? date1 : date1.toDate();
    const d2 = date2 instanceof Date ? date2 : date2.toDate();
    
    // Compare year, month, and day
    return d1.getFullYear() === d2.getFullYear() &&
           d1.getMonth() === d2.getMonth() &&
           d1.getDate() === d2.getDate();
  };

  // Get current tabs based on loan type
  const getCurrentTabs = () => {
    switch (selectedLoanType) {
      case 'Acquisition':
        return acquisitionTabs;
      case 'workingCapital':
        return workingCapitalTabs;
      case 'calls':
        return callsTabs;
      case 'lenderMatch':
        return lenderMatchTabs;
      case 'starred':
        return [];
      default:
        return acquisitionTabs;
    }
  };

  // Update handleLoanTypeChange to include prequalification
  const handleLoanTypeChange = (type) => {
    setSelectedLoanType(type);
    setActiveSection(type === 'prequalification' ? 'prequalifications' : 'recentActivity');
  };

  // Modify the fetch functions to filter by loan type
  const fetchLoans = async (status) => {
    try {
      const loansRef = collection(db, 'loanApplications');
      let q;

      if (selectedLoanType === 'workingCapital') {
        q = query(
          loansRef,
          where('loanPurpose', '==', 'workingCapital'),
          where('loanStatus', '==', status)
        );
      } else {
        q = query(
          loansRef,
          where('loanPurpose', '==', 'Acquisition'),
          where('status', '==', status)
        );
      }

      const querySnapshot = await getDocs(q);
      return querySnapshot.docs.map((doc) => ({
        id: doc.id,
        ...doc.data(),
      }));
    } catch (error) {
      console.error('Error fetching loans:', error);
      return [];
    }
  };

  useEffect(() => {
    const fetchRecentUsers = async () => {
      try {
        const usersRef = collection(db, 'loanApplications');
        const q = query(usersRef, orderBy('createdAt', 'desc'), limit(10));
        const querySnapshot = await getDocs(q);
        const usersData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setRecentUsers(usersData);
      } catch (error) {
        console.error('Error fetching recent users:', error);
      }
    };

    const fetchPreLenderLoans = async () => {
      try {
        const loansRef = collection(db, 'loanApplications');
        const q = query(loansRef, where('status', '==', 'Pre-lender'));
        const querySnapshot = await getDocs(q);
        const loansData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setPreLenderLoans(loansData);
      } catch (error) {
        console.error('Error fetching pre-lender loans:', error);
      }
    };

    const fetchInTalksLoans = async () => {
      try {
        const loansRef = collection(db, 'loanApplications');
        const q = query(loansRef, where('status', 'in', ['in-talks', 'under_review']));
        const querySnapshot = await getDocs(q);
        const loansData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setInTalksLoans(loansData);
      } catch (error) {
        console.error('Error fetching in-talks loans:', error);
      }
    };

    const fetchClosedDeals = async () => {
      try {
        const loansRef = collection(db, 'loanApplications');
        const q = query(loansRef, where('status', '==', 'closed'));
        const querySnapshot = await getDocs(q);
        const loansData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setClosedDeals(loansData);
      } catch (error) {
        console.error('Error fetching closed deals:', error);
      }
    };

    const fetchPrequalifications = async () => {
      console.log('Fetching prequalifications...');
      try {
        // Get brokerage profiles so both listing and borrower records show ownership.
        const brokersRef = collection(db, 'brokers');
        const brokersSnapshot = await getDocs(brokersRef);
        const brokersMap = {};
        
        brokersSnapshot.docs.forEach(doc => {
          const userData = doc.data();
          brokersMap[doc.id] = {
              id: doc.id,
              name: userData.brokerageName || userData.name || `${userData.firstName || ''} ${userData.lastName || ''}`.trim() || 'Unknown Broker',
              email: userData.email,
            };
        });

        console.log('Brokers map:', brokersMap);

        // Then get prequalifications
        const prequalRef = collection(db, 'prequalificationApplications');
        const prequalSnapshot = await getDocs(prequalRef);
        
        const prequalificationsWithBrokers = prequalSnapshot.docs.map(doc => {
          const data = doc.data();
          const brokerId = data.brokerId || data.userId;
          const brokerInfo = brokersMap[brokerId];
          
          console.log(`Mapping prequalification for broker ${brokerId}:`, brokerInfo);

          return {
            id: doc.id,
            ...data,
            brokerName: brokerInfo?.name || 'Unknown Broker',
            brokerEmail: brokerInfo?.email || 'No email',
            broker: brokerInfo
          };
        });

        console.log('Final prequalifications with brokers:', prequalificationsWithBrokers);
        setPrequalifications(prequalificationsWithBrokers);

        const loansSnapshot = await getDocs(collection(db, 'loanApplications'));
        const borrowerRecords = loansSnapshot.docs
          .map(loanDoc => ({ id: loanDoc.id, ...loanDoc.data() }))
          .filter(item => item.source === 'broker_referral' || (!item.businessName && item.loanPurpose === 'Acquisition' && item.email))
          .map(item => ({
            ...item,
            recordType: 'borrower',
            brokerName: brokersMap[item.brokerId]?.name || item.brokerName || (item.brokerId ? 'Unknown Broker' : 'Direct / Lended'),
            brokerEmail: brokersMap[item.brokerId]?.email || '',
          }));
        setBorrowerPrequalifications(borrowerRecords);
      } catch (error) {
        console.error('Error fetching prequalifications:', error);
        console.error('Error details:', error.message);
      }
    };

    fetchRecentUsers();
    fetchPreLenderLoans();
    fetchInTalksLoans();
    fetchClosedDeals();
    fetchPrequalifications();
  }, [selectedLoanType]);

  // Modify the fetchAllLoans function
  const fetchAllLoans = async () => {
    try {
      const loansRef = collection(db, 'loanApplications');
      let q;
      
      if (selectedLoanType === 'starred') {
        // Query for starred applications - add limit and orderBy
        q = query(
          loansRef,
          where('starred', '==', true),
          orderBy('createdAt', 'desc'),
          limit(500) // Limit to 500 most recent starred loans
        );
        
        const querySnapshot = await getDocs(q);
        const loansData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setLoans(loansData);
      } else if (selectedLoanType === 'calls') {
        const startOfDay = new Date(selectedDate);
        startOfDay.setHours(0, 0, 0, 0);
        
        const endOfDay = new Date(selectedDate);
        endOfDay.setHours(23, 59, 59, 999);
        
        // Use orderBy and limit to reduce reads - fetch most recent 500 loans
        q = query(
          loansRef,
          orderBy('createdAt', 'desc'),
          limit(500)
        );
        
        const querySnapshot = await getDocs(q);
        const loansData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        const filteredLoans = loansData.filter(loan => {
          const loanDate = loan.displayDate?.toDate?.() || loan.createdAt?.toDate?.() || new Date(loan.createdAt);
          const loanLocalDate = new Date(loanDate).toLocaleDateString();
          const selectedLocalDate = startOfDay.toLocaleDateString();
          
          return loanLocalDate === selectedLocalDate;
        });

        setLoans(filteredLoans);
      } else {
        // Original query for non-calls views - add limit and orderBy
        q = query(
          loansRef,
          where('loanPurpose', '==', selectedLoanType === 'workingCapital' ? 'workingCapital' : 'Acquisition'),
          orderBy('createdAt', 'desc'),
          limit(500) // Limit to 500 most recent loans
        );
        const querySnapshot = await getDocs(q);
        const loansData = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));
        setLoans(loansData);
      }
    } catch (error) {
      console.error('Error fetching loans:', error);
      setLoans([]);
    }
  };

  // Use it in the useEffect
  useEffect(() => {
    fetchAllLoans();
    fetchOffMarketSearchers();
  }, [selectedDate, selectedLoanType]);

  const handleLogout = () => {
    const auth = getAuth();
    signOut(auth)
      .then(() => {
        navigate('/login');
      })
      .catch((error) => {
        console.error('Sign out error:', error);
      });
  };

  const handleCardClick = (loan) => {
    console.log('Card clicked:', loan);
    if (activeSection === 'prequalifications') {
      console.log('Navigating to prequalification details:', `/prequalification-details/${loan.id}`);
      navigate(`/prequalification-details/${loan.id}`);
    } else {
      navigate(`/loan-details/${loan.id}`);
    }
  };

  const handleSearch = (event) => {
    const term = event.target.value;
    setSearchTerm(term);
  };

  const changeDate = (direction) => {
    const newDate = new Date(selectedDate);
    if (direction === 'prev') {
      newDate.setDate(newDate.getDate() - 1);
    } else if (direction === 'next') {
      newDate.setDate(newDate.getDate() + 1);
    }
    setSelectedDate(newDate);
  };

  const navigateToLoanDate = (loan) => {
    let displayDate;
    
    try {
      if (loan.called) {
        // For called loans, try followUpDate first, then displayDate
        if (loan.followUpDate?.toDate) {
          displayDate = loan.followUpDate.toDate();
        } else if (loan.displayDate?.toDate) {
          displayDate = loan.displayDate.toDate();
        } else if (loan.followUpDate) {
          displayDate = new Date(loan.followUpDate);
        } else if (loan.displayDate) {
          displayDate = new Date(loan.displayDate);
        }
      } else {
        // For uncalled loans, try createdAt
        if (loan.createdAt?.toDate) {
          displayDate = loan.createdAt.toDate();
        } else if (loan.createdAt) {
          displayDate = new Date(loan.createdAt);
        }
      }
      
      // If we couldn't get a valid date, use today's date
      if (!displayDate || isNaN(displayDate.getTime())) {
        displayDate = new Date();
        console.warn('Using current date as fallback for loan:', loan.id);
      }
      
      setSelectedDate(displayDate);
    } catch (error) {
      console.error('Error processing date for loan:', loan.id, error);
      // Fallback to current date if there's an error
      setSelectedDate(new Date());
    }
  };

  useEffect(() => {
    const searchAcrossLoans = async () => {
      if (!searchTerm) {
        setSearchResults([]);
        setIsSearching(false);
        return;
      }

      setIsSearching(true);
      const searchTermLower = searchTerm.toLowerCase();

      try {
        const loansRef = collection(db, 'loanApplications');
        // Add limit and orderBy to reduce reads - search only in most recent 500 loans
        const q = query(
          loansRef,
          orderBy('createdAt', 'desc'),
          limit(500)
        );
        const querySnapshot = await getDocs(q);
        const allLoans = querySnapshot.docs.map((doc) => ({
          id: doc.id,
          ...doc.data(),
        }));

        // Filter based on search term
        const filteredLoans = allLoans.filter(loan => {
          // Check business name
          const businessNameMatch = loan.businessName?.toLowerCase().includes(searchTermLower);
          
          // Check first and last name
          const fullNameMatch = `${loan.firstName} ${loan.lastName}`.toLowerCase().includes(searchTermLower);
          
          // Check primary email
          const emailMatch = loan.email?.toLowerCase().includes(searchTermLower);
          
          // Check emails array
          const emailsArrayMatch = Array.isArray(loan.emails) && 
            loan.emails.some(email => email.toLowerCase().includes(searchTermLower));

          return businessNameMatch || fullNameMatch || emailMatch || emailsArrayMatch;
        });

        setSearchResults(filteredLoans);
      } catch (error) {
        console.error('Error searching loans:', error);
        setSearchResults([]);
      } finally {
        setIsSearching(false);
      }
    };

    // Debounce the search to avoid too many requests
    const timeoutId = setTimeout(() => {
      searchAcrossLoans();
    }, 300);

    return () => clearTimeout(timeoutId);
  }, [searchTerm]);

  const getDaysInStage = (loan) => {
    // Mirror the logic from loandetails.js
    if (!loan) return 0;
    const status = loan.loanStatus || LoanStatuses.NEW_APPLICATION;
    let relevantDate;

    // Use specific timestamps if available, otherwise fall back
    switch(status) {
        case LoanStatuses.SUBMITTED:
            relevantDate = loan.submittedAt; // Use the specific submitted timestamp
            break;
        case LoanStatuses.UNDERWRITING:
            relevantDate = loan.underwritingAt || loan.submittedAt;
            break;
        case LoanStatuses.PREQUALIFIED_FOR:
             // Check for variations in timestamp field names
             relevantDate = loan.prequalified_forAt || loan.prequalifiedAt || loan.submittedAt;
            break;
        case LoanStatuses.TERM_SHEET_EXTENDED:
            relevantDate = loan.term_sheet_extendedAt || loan.submittedAt;
            break;
        case LoanStatuses.DOCUMENT_CLARIFICATION:
            relevantDate = loan.document_clarificationAt || loan.submittedAt;
            break;
        case LoanStatuses.CLOSING:
             relevantDate = loan.closingAt || loan.submittedAt;
             break;
        case LoanStatuses.FUNDED:
            relevantDate = loan.fundedAt || loan.submittedAt;
            break;
        case LoanStatuses.REJECTED:
             relevantDate = loan.rejectedAt || loan.createdAt;
             break;
        case LoanStatuses.NEW_APPLICATION:
        default:
            // Use specific status timestamp (lowercase_with_underscores) or createdAt
            const statusKey = status.toLowerCase().replace(/\s+/g, '_') + 'At';
            relevantDate = loan[statusKey] || loan.createdAt;
            break;
    }

    // Final fallback to createdAt if no relevant date found
    return getDaysSince(relevantDate || loan.createdAt);
  };

  // Generate notifications based on loan activities
  const generateNotifications = (loans) => {
    const notifications = [];
    const today = new Date();
    const todayString = today.toISOString().split('T')[0];

    loans.forEach(loan => {
      const displayName = loan.businessName || `${loan.firstName} ${loan.lastName}`.trim();
      
      // 1. Follow-ups due TODAY and OVERDUE
      if (loan.followUpDate) {
        const isOverdue = loan.followUpDate < todayString;
        const isDueToday = loan.followUpDate === todayString;
        
        if (isOverdue || isDueToday) {
          const reasonText = loan.followUpReason ? ` - ${loan.followUpReason}` : '';
          notifications.push({
            id: `followup-${isOverdue ? 'overdue' : 'today'}-${loan.id}`,
            type: 'followup_today',
            title: isOverdue ? 'Overdue Follow-up' : 'Follow-up Due Today',
            message: `${displayName}${reasonText}`,
            loanId: loan.id,
            priority: 'high',
            timestamp: new Date()
          });
        }
      }

      // 2. New applications (last 24 hours)
      if (loan.createdAt) {
        const createDate = loan.createdAt.toDate ? loan.createdAt.toDate() : new Date(loan.createdAt);
        const timeDiff = today - createDate;
        const hoursDiff = timeDiff / (1000 * 60 * 60);
        
        if (hoursDiff <= 24) {
          notifications.push({
            id: `new-${loan.id}`,
            type: 'new_application',
            title: 'New Application',
            message: `${displayName} submitted a new application ${Math.floor(hoursDiff)}h ago`,
            loanId: loan.id,
            priority: 'medium',
            timestamp: createDate
          });
        }
      }
    });

    // Sort by priority and timestamp
    const priorityOrder = { high: 0, medium: 1, low: 2 };
    return notifications.sort((a, b) => {
      if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
        return priorityOrder[a.priority] - priorityOrder[b.priority];
      }
      return b.timestamp - a.timestamp;
    });
  };

  // Fetch recent document uploads from documentMetadata
  const fetchRecentDocumentUploads = async () => {
    try {
      const documentsRef = collection(db, 'documentMetadata');
      // Add limit and orderBy to reduce reads - only fetch most recent 200 documents
      const q = query(
        documentsRef,
        orderBy('uploadedAt', 'desc'),
        limit(200)
      );
      const querySnapshot = await getDocs(q);
      const uploads = [];
      const today = new Date();

      querySnapshot.docs.forEach(doc => {
        const data = doc.data();
        if (data.uploadedAt) {
          const uploadDate = data.uploadedAt.toDate ? data.uploadedAt.toDate() : new Date(data.uploadedAt);
          const timeDiff = today - uploadDate;
          const hoursDiff = timeDiff / (1000 * 60 * 60);
          
          // Only include uploads from last 24 hours
          if (hoursDiff <= 24) {
            uploads.push({
              id: `upload-${doc.id}`,
              type: 'recent_upload',
              title: 'Recent Document Upload',
              message: `Document "${data.fileName || 'Unknown'}" uploaded ${Math.floor(hoursDiff)}h ago`,
              loanId: data.loanApplicationId,
              priority: 'medium',
              timestamp: uploadDate,
              fileName: data.fileName
            });
          }
        }
      });

      return uploads;
    } catch (error) {
      console.error('Error fetching document uploads:', error);
      return [];
    }
  };

  const getCurrentChecklist = (loan) => {
    if (!loan) return [];
    // Directly use the loanStatus to get the checklist
    return statusChecklists[loan.loanStatus || LoanStatuses.NEW_APPLICATION] || [];
  };

  const isChecklistComplete = (loan) => {
    const checklist = getCurrentChecklist(loan);
    if (!checklist || checklist.length === 0) {
      return true; // No checklist means it's complete
    }
    const checklistState = loan.statusChecklistState || {};
    return checklist.every(item => checklistState[item] === true);
  };

  const getAlertMessage = (loan) => {
    // Mirror the logic from loandetails.js
    if (!loan || !loan.loanStatus) return null;

    const loanStatus = loan.loanStatus;
    const daysInStatus = getDaysInStage(loan); // Use updated calculation
    const checklistState = loan.statusChecklistState || {};
    const submittedAt = loan.submittedAt; // Get the submission timestamp

    // Handle SUBMITTED status
    if (loanStatus === LoanStatuses.SUBMITTED) {
      if (checklistState['Send initial package to bank'] === true) {
        const daysSinceSubmission = submittedAt ? getDaysSince(submittedAt) : 0;
        if (daysSinceSubmission >= 4) return "Follow up with bank (Day 4+)";
        if (daysSinceSubmission >= 2) return "Follow up with bank (Day 2)";
      }
      return null;
    }

    // Handle other explicit statuses
    switch (loanStatus) {
      case LoanStatuses.NEW_APPLICATION:
        if (daysInStatus >= 5) return "Follow up with client (Day 5+)";
        if (daysInStatus >= 2) return "Follow up with client (Day 2)";
        return null;
      case LoanStatuses.PREQUALIFIED_FOR:
      case LoanStatuses.TERM_SHEET_EXTENDED:
        if (daysInStatus >= 4) return "Follow up (Day 4+)";
        return null;
      case LoanStatuses.DOCUMENT_CLARIFICATION:
        if (daysInStatus >= 4) return "Follow up with client (Day 4+)";
        if (daysInStatus >= 2) return "Follow up with client (Day 2)";
        return null;
      case LoanStatuses.CLOSING:
        if (daysInStatus >= 5) return "Follow up with bank (Day 5+)";
        return null;
      default:
        return null;
    }
  };

  const getLoanStatus = (loan) => {
    // This function determines the 'urgency' level for sorting/display
    if (!loan) return 'onTrack';
    const loanStatus = loan.loanStatus || LoanStatuses.NEW_APPLICATION;
    const alertMessage = getAlertMessage(loan);
    const checklistComplete = isChecklistComplete(loan);

    // This logic primarily applies to the 'starred' view for prioritization
    if (selectedLoanType === 'starred') {
        // If checklist is not complete for the *current* status, it's urgent
        if (!checklistComplete) {
            return 'urgent';
        }
        // If checklist is complete, but a time-based alert is active, needs follow-up
        if (alertMessage) {
            return 'followUpNeeded';
        }
        // Otherwise, it's on track for its current status
        return 'onTrack';
    }

    // Fallback for non-starred sections (uses simpler time-based warning)
    const days = getDaysInStage(loan);
    if (loanStatus === LoanStatuses.UNDERWRITING && days > 20) return 'warning';
    if (loanStatus !== LoanStatuses.FUNDED && loanStatus !== LoanStatuses.REJECTED && loanStatus !== LoanStatuses.UNDERWRITING && days > 7) return 'warning';
    return 'onTrack'; // Default for non-starred view if no warning
  };

  const getNextTask = (loan) => {
    // Mirror the logic from loandetails.js for consistency
    if (!loan) return { text: "No Data", status: "text-gray-700 bg-gray-50 ring-gray-600/20" };

    const days = getDaysInStage(loan);
    const calculatedUrgency = getLoanStatus(loan); // 'urgent', 'followUpNeeded', 'onTrack' (for starred) or 'warning', 'onTrack' (for others)
    const loanStatus = loan.loanStatus || LoanStatuses.NEW_APPLICATION;
    const checklist = getCurrentChecklist(loan);
    const checklistState = loan.statusChecklistState || {};
    const alertMessage = getAlertMessage(loan);
    const submittedAt = loan.submittedAt;

    if (selectedLoanType === 'starred') {
      // If Urgent (Checklist incomplete)
      if (calculatedUrgency === 'urgent') {
        const firstUncheckedItem = checklist.find(item => !checklistState[item]);
        let taskText = firstUncheckedItem ? `Action needed: ${firstUncheckedItem}` : 'Urgent: Review Checklist';
        return { text: taskText, status: "text-red-700 bg-red-50 ring-red-600/20" };
      }

      // If Follow Up Needed (Time-based alert is active)
      if (calculatedUrgency === 'followUpNeeded') {
        let taskText = alertMessage || 'Follow-up Needed';
        // Add context based on status
        if (loanStatus === LoanStatuses.NEW_APPLICATION) {
          taskText = `Awaiting submission - ${taskText} • ${days} day${days === 1 ? '' : 's'}`;
        } else if (loanStatus === LoanStatuses.SUBMITTED && checklistState['Send initial package to bank'] === true) {
           const daysSinceSubmission = submittedAt ? getDaysSince(submittedAt) : days;
           taskText = `Package Sent - ${taskText} • ${daysSinceSubmission} day${daysSinceSubmission === 1 ? '' : 's'}`;
        } else {
           taskText += ` • ${days} day${days === 1 ? '' : 's'}`; // Add day count for other statuses
        }
        return { text: taskText, status: "text-yellow-700 bg-yellow-50 ring-yellow-600/20" };
      }

      // If On Track (Checklist complete, no alert)
      if (calculatedUrgency === 'onTrack') {
         if (loanStatus === LoanStatuses.NEW_APPLICATION && checklistState['Call to explain process'] === true) {
            return { text: `Awaiting Documents • ${days} day${days === 1 ? '' : 's'}`, status: "text-blue-700 bg-blue-50 ring-blue-600/20" };
         }
         if (loanStatus === LoanStatuses.SUBMITTED && checklistState['Send initial package to bank'] === true) {
            const daysAwaitingFeedback = submittedAt ? getDaysSince(submittedAt) : days;
            return { text: `Awaiting Bank Feedback • ${daysAwaitingFeedback} day${daysAwaitingFeedback === 1 ? '' : 's'}`, status: statuses['Submitted'] || "text-yellow-700 bg-yellow-50 ring-yellow-600/20" };
         }
         // Other 'onTrack' states will fall through to the general logic below
      }
    }

    // General Logic (also applies to 'onTrack' starred loans not caught above)
    // Provides a general task based on the current status and adds day count
    const statusText = loanStatus.replace(/_/g, ' '); // Make status readable
    let taskDescription = `Status: ${statusText}`;
    let taskStatusStyle = statuses[loanStatus] || "text-gray-700 bg-gray-50 ring-gray-600/20"; // Default style
    const isComplete = isChecklistComplete(loan); // Call function here

    switch (loanStatus) {
        case LoanStatuses.FUNDED:
            taskDescription = `Funded`;
            break;
        case LoanStatuses.REJECTED:
            taskDescription = `Rejected`;
             break;
        case LoanStatuses.NEW_APPLICATION:
            taskDescription = `Awaiting Documents • ${days} day${days === 1 ? '' : 's'}`;
            break;
        case LoanStatuses.SUBMITTED:
             // If checklist item 'Send...' is not checked, that's the implied next task
             if (!checklistState['Send initial package to bank']) {
                taskDescription = `Submitted - Send Package • ${days} day${days === 1 ? '' : 's'}`;
             } else {
                taskDescription = `Submitted - Awaiting Feedback • ${days} day${days === 1 ? '' : 's'}`;
             }
            break;
        // Add other specific descriptions if needed
        default:
            taskDescription = `${statusText} • ${days} day${days === 1 ? '' : 's'}`;
            break;
    }

     // Apply warning style from getLoanStatus if applicable (for non-starred view)
     if (selectedLoanType !== 'starred' && calculatedUrgency === 'warning') {
        taskStatusStyle = "text-orange-700 bg-orange-50 ring-orange-600/20"; // Example warning style
     } else if (loanStatus === LoanStatuses.FUNDED || loanStatus === LoanStatuses.REJECTED || isComplete) { // Use the isComplete variable
         // Use green 'onTrack' style if funded, rejected, or checklist complete without alerts
         taskStatusStyle = "text-green-700 bg-green-50 ring-green-600/20";
     } else {
         // Use the status-specific style otherwise
         taskStatusStyle = statuses[loanStatus] || taskStatusStyle;
     }

    return { text: taskDescription, status: taskStatusStyle };
  };

  const getLoanStage = (loan) => {
    const status = loan?.loanStatus;
    if (!status) return 'awaitingDocs'; // Default stage

    switch (status) {
      case LoanStatuses.FUNDED: return 'funded';
      case LoanStatuses.UNDERWRITING: return 'underwriting';
      case LoanStatuses.PREQUALIFIED_FOR:
      case LoanStatuses.TERM_SHEET_EXTENDED: return 'prequalified';
      case LoanStatuses.DOCUMENT_CLARIFICATION: return 'documentReview';
      case LoanStatuses.SUBMITTED: return 'submitted'; // Renamed stage from 'docsUploaded'
      case LoanStatuses.NEW_APPLICATION: return 'awaitingDocs';
      case LoanStatuses.CLOSING: return 'closing'; // Added closing stage mapping
      case LoanStatuses.REJECTED: return 'rejected'; // Added rejected stage mapping
      default: return 'awaitingDocs'; // Fallback
    }
  };

  const QuickStatsBar = ({ loans, onFilterClick, activeFilter }) => {
    // Recalculate counts based on the final getLoanStatus logic
    const urgentCount = loans.filter(loan => getLoanStatus(loan) === 'urgent').length;
    const followUpNeededCount = loans.filter(loan => getLoanStatus(loan) === 'followUpNeeded').length;
    const onTrackCount = loans.filter(loan => getLoanStatus(loan) === 'onTrack').length;

    const stats = [
      { key: 'urgent', label: 'Urgent Action Needed', count: urgentCount, description: 'Checklist incomplete', filterValue: 'urgent',
        icon: FireIcon, iconBg: 'bg-red-50 text-red-600', num: 'text-red-600', bar: 'bg-red-500', dot: 'bg-red-500', ring: 'ring-red-500' },
      { key: 'followUpNeeded', label: 'Follow Up Needed', count: followUpNeededCount, description: 'Follow-up due', filterValue: 'followUpNeeded',
        icon: ClockOutlineIcon, iconBg: 'bg-amber-50 text-amber-600', num: 'text-amber-600', bar: 'bg-amber-500', dot: 'bg-amber-500', ring: 'ring-amber-500' },
      { key: 'onTrack', label: 'On Track', count: onTrackCount, description: 'Regular progress', filterValue: 'onTrack',
        icon: CheckCircleOutlineIcon, iconBg: 'bg-emerald-50 text-emerald-600', num: 'text-emerald-600', bar: 'bg-emerald-500', dot: 'bg-emerald-500', ring: 'ring-emerald-500' },
    ];
    const total = stats.reduce((sum, s) => sum + s.count, 0) || 1;

    return (
      <div className="mb-6 space-y-4">
        {/* Segmented status bar */}
        <div className="bg-white rounded-2xl border border-gray-200 shadow-sm p-4">
          <div className="flex h-2 w-full overflow-hidden rounded-full bg-gray-100">
            {stats.map(s => (
              s.count > 0 && <div key={s.key} className={s.bar} style={{ width: `${(s.count / total) * 100}%` }} title={`${s.label}: ${s.count}`} />
            ))}
          </div>
          <div className="mt-3 flex flex-wrap gap-x-6 gap-y-2">
            {stats.map(s => (
              <div key={s.key} className="flex items-center gap-1.5">
                <span className={`h-2 w-2 rounded-full ${s.dot}`} />
                <span className="text-sm text-gray-600">{s.label}</span>
                <span className="text-sm font-semibold text-gray-900">{s.count}</span>
              </div>
            ))}
          </div>
        </div>

        {/* Stat cards */}
        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4">
          {stats.map(s => {
            const Icon = s.icon;
            const pct = Math.round((s.count / total) * 100);
            const isActive = activeFilter === s.filterValue;
            return (
              <button
                key={s.key}
                onClick={() => onFilterClick(isActive ? null : s.filterValue)}
                className={`text-left bg-white rounded-2xl border shadow-sm p-5 transition-all hover:shadow-md relative ${isActive ? `ring-2 ${s.ring} border-transparent` : 'border-gray-200'}`}
              >
                <div className="flex items-center justify-between">
                  <div className={`h-9 w-9 rounded-lg flex items-center justify-center ${s.iconBg}`}>
                    <Icon className="h-5 w-5" />
                  </div>
                  {isActive ? (
                    <span className="text-xs font-medium text-indigo-600">Clear ×</span>
                  ) : (
                    <span className="text-xs font-medium text-gray-400">{pct}%</span>
                  )}
                </div>
                <p className="mt-3 text-xs font-medium uppercase tracking-wider text-gray-400">{s.label}</p>
                <p className={`mt-1 text-3xl font-bold leading-none ${s.num}`}>{s.count}</p>
                <p className="mt-1 text-xs text-gray-400">{s.description}</p>
                <div className="mt-3 h-1.5 w-full rounded-full bg-gray-100 overflow-hidden">
                  <div className={`h-full rounded-full ${s.bar}`} style={{ width: `${pct}%` }} />
                </div>
              </button>
            );
          })}
        </div>
      </div>
    );
  };

  const handleToggleStar = async (loanId, currentStatus) => {
    const loanRef = doc(db, 'loanApplications', loanId);
    try {
      await updateDoc(loanRef, {
        starred: !currentStatus
      });
      console.log(`Loan ${loanId} star status updated successfully.`);
      setLoans(prevLoans => prevLoans.filter(l => l.id !== loanId));
    } catch (error) {
      console.error('Error updating star status:', error);
    }
  };

  const renderSection = () => {
    let loansToRender;
    let sectionTitle;

    if (searchTerm) {
       loansToRender = searchResults;
       sectionTitle = "Search Results";
     } else if (selectedLoanType === 'starred') {
       let filteredLoans = loans;

       // Apply status filter
       if (statusFilter) {
         filteredLoans = filteredLoans.filter(loan => getLoanStatus(loan) === statusFilter);
       }

       // Apply stage filter
       if (selectedStage) {
         filteredLoans = filteredLoans.filter(loan => getLoanStage(loan) === selectedStage);
       }

       // Sort the filtered list
       loansToRender = [...filteredLoans].sort((a, b) => {
         const statusA = getLoanStatus(a);
         const statusB = getLoanStatus(b);
         const priority = { urgent: 0, followUpNeeded: 1, onTrack: 2 };
         return priority[statusA] - priority[statusB];
       });


      return (
        <div className="px-4 sm:px-6 lg:px-8">
          {/* Stats Section */}
           {displayStats && (
             <div className="mb-8">
               <h3 className="text-base font-semibold text-gray-900">Current Stats</h3>
               <dl className="mt-5 grid grid-cols-1 divide-y divide-gray-200 overflow-hidden rounded-lg bg-white shadow md:grid-cols-3 md:divide-x md:divide-y-0">
                 {displayStats.map((item) => (
                   <div key={item.name} className="px-4 py-5 sm:p-6">
                     <dt className="text-base font-normal text-gray-900">{item.name}</dt>
                     <dd className="mt-1 flex items-baseline justify-between md:block lg:flex">
                       <div className="flex items-baseline text-2xl font-semibold text-indigo-600">
                         {item.stat}
                         <span className="ml-2 text-sm font-medium text-gray-500">{item.previousStat}</span>
                       </div>
                     </dd>
                   </div>
                 ))}
               </dl>
             </div>
           )}

          {/* Quick Stats Bar */}
          <QuickStatsBar
            loans={loans}
            onFilterClick={setStatusFilter}
            activeFilter={statusFilter}
          />

          {/* Stage Navigation */}
          <div className="mb-6">
             <div className="bg-white rounded-lg shadow p-4">
               <h4 className="text-sm font-medium text-gray-500 mb-3">Filter by Stage</h4>
               <div className="flex flex-wrap gap-2">
                 {LOAN_STAGES.map((stage) => {
                   const stageCount = loans.filter(loan => getLoanStage(loan) === stage.id).length;
                   return (
                     <button
                       key={stage.id}
                       onClick={() => setSelectedStage(selectedStage === stage.id ? null : stage.id)}
                       className={`inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium
                         ${selectedStage === stage.id
                           ? `bg-${stage.color}-100 text-${stage.color}-800 ring-2 ring-${stage.color}-600`
                           : `bg-${stage.color}-50 text-${stage.color}-600 hover:bg-${stage.color}-100`
                         }`}
                     >
                       {stage.label}
                       <span className={`ml-2 px-2 py-0.5 rounded-full text-xs bg-${stage.color}-100`}>
                         {stageCount}
                       </span>
                     </button>
                   );
                 })}
                 {selectedStage && (
                   <button
                     onClick={() => setSelectedStage(null)}
                     className="inline-flex items-center px-3 py-1.5 rounded-full text-sm font-medium text-gray-500 hover:bg-gray-100"
                   >
                     Clear Filter
                     <span className="ml-1">×</span>
                   </button>
                 )}
               </div>
             </div>
           </div>

          {/* Filter indicators */}
          {(statusFilter || selectedStage) && (
             <div className="mb-4 flex items-center justify-between bg-indigo-50 p-3 rounded-lg">
               <span className="text-indigo-700">
                 Showing {loansToRender.length} {selectedStage ? LOAN_STAGES.find(s => s.id === selectedStage)?.label : ''}
                 {statusFilter ? ` ${statusFilter === 'onTrack' ? 'on-track' : (statusFilter === 'followUpNeeded' ? 'follow-up needed' : statusFilter)}` : ''} applications
               </span>
               <div className="flex gap-2">
                 {statusFilter && (
                   <button
                     onClick={() => setStatusFilter(null)}
                     className="text-indigo-600 hover:text-indigo-800"
                   >
                     Clear status filter
                   </button>
                 )}
                 {selectedStage && (
                   <button
                     onClick={() => setSelectedStage(null)}
                     className="text-indigo-600 hover:text-indigo-800"
                   >
                     Clear stage filter
                   </button>
                 )}
               </div>
             </div>
           )}

          {/* Alphabetical Navigation */}
          <div className="mb-4 sticky top-0 z-10 bg-white shadow rounded-lg p-2">
            <div className="flex justify-center space-x-1">
              {alphabet.map((letter) => {
                const hasLoans = loansToRender.some(loan => getFirstChar(loan) === letter);
                return (
                  <button
                    key={letter}
                    onClick={() => {
                      const element = document.getElementById(`section-${letter}`);
                      if (element) { element.scrollIntoView({ behavior: 'smooth' }); }
                    }}
                    className={`px-2 py-1 text-sm font-medium rounded ${
                      hasLoans
                        ? 'text-indigo-600 hover:bg-indigo-50 focus:outline-none focus:ring-2 focus:ring-indigo-500'
                        : 'text-gray-300 cursor-not-allowed'
                    }`}
                    disabled={!hasLoans}
                  >
                    {letter}
                  </button>
                );
              })}
            </div>
          </div>

          {/* Grouped Loans List - Restoring the full card structure */}
          <div className="space-y-8">
            {alphabet.map((letter) => {
              const letterLoans = loansToRender.filter(loan => getFirstChar(loan) === letter);
              if (letterLoans.length === 0) return null;

              return (
                <div key={letter} id={`section-${letter}`} className="relative">
                  <div className="sticky top-44 z-10 bg-gray-50/95 py-2 backdrop-blur">
                    <h3 className="flex h-8 w-8 items-center justify-center rounded-lg bg-white text-sm font-bold text-indigo-600 shadow-sm ring-1 ring-gray-200">{letter}</h3>
                  </div>
                  <ul role="list" className="grid grid-cols-1 gap-4 mt-2 lg:grid-cols-2">
                    {letterLoans.map((loan) => {
                      const loanStatusClass = getLoanStatus(loan);
                      let statusAccent = 'bg-emerald-500';
                      let statusSurface = 'bg-emerald-50 text-emerald-700';
                      if (loanStatusClass === 'urgent') {
                          statusAccent = 'bg-red-500';
                          statusSurface = 'bg-red-50 text-red-700';
                      } else if (loanStatusClass === 'followUpNeeded') {
                          statusAccent = 'bg-amber-500';
                          statusSurface = 'bg-amber-50 text-amber-700';
                      }
                      const nextTask = getNextTask(loan);

                      return (
                        <li key={loan.id} className="group relative overflow-hidden rounded-2xl border border-gray-200 bg-white shadow-sm transition-all hover:-translate-y-0.5 hover:shadow-md">
                          <div className={`absolute inset-x-0 top-0 h-1 ${statusAccent}`} />
                          <button
                              onClick={() => handleToggleStar(loan.id, loan.starred)}
                              className={`absolute top-4 right-4 rounded-lg p-1.5 transition-colors focus:outline-none ${loan.starred ? 'bg-amber-50 text-amber-500' : 'text-gray-300 hover:bg-amber-50 hover:text-amber-500'}`}
                              title={loan.starred ? "Unstar this application" : "Star this application"}
                             >
                              <StarIcon className="h-6 w-6" aria-hidden="true" />
                            </button>
                          <div className="p-5 pt-6">
                            <div className="min-w-0 flex-1">
                              <div className="flex items-start gap-3 pr-10">
                                <div className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-xl bg-indigo-50 text-sm font-bold text-indigo-700">
                                  {(loan.businessName || loan.firstName || 'L').trim()[0].toUpperCase()}
                                </div>
                                <div className="min-w-0">
                                  <p className="truncate text-base font-semibold text-gray-900">{loan.businessName || `${loan.firstName} ${loan.lastName}`.trim()}</p>
                                  <p className="mt-0.5 truncate text-xs text-gray-500">{[loan.firstName, loan.lastName].filter(Boolean).join(' ')}{loan.email ? ` · ${loan.email}` : ''}</p>
                                </div>
                                <p
                                  className={classNames(
                                    statuses[loan.loanPurpose === 'Acquisition' ? 'Acquisition' : loan.loanStatus] || 'text-gray-600 bg-gray-50 ring-gray-500/10',
                                    'ml-auto mt-0.5 whitespace-nowrap rounded-full px-2.5 py-1 text-xs font-medium ring-1 ring-inset'
                                  )}
                                >
                                  {loan.loanPurpose === 'Acquisition' ? 'Acquisition' : loan.loanStatus}
                                </p>
                              </div>
                              <div className="mt-5 grid grid-cols-2 gap-3 border-y border-gray-100 py-4">
                                <div>
                                  <p className="text-xs font-medium uppercase tracking-wider text-gray-400">Loan Amount</p>
                                  <p className="mt-1 text-lg font-bold text-gray-900">{Number.isFinite(Number(loan.loanSize)) ? `$${Number(loan.loanSize).toLocaleString()}` : 'Not provided'}</p>
                                </div>
                                <div>
                                  <p className="text-xs font-medium uppercase tracking-wider text-gray-400">Created</p>
                                  <p className="mt-1 text-sm font-semibold text-gray-700">{loan.createdAt?.toDate?.().toLocaleDateString() || (loan.createdAt ? new Date(loan.createdAt).toLocaleDateString() : 'Unknown')}</p>
                                </div>
                              </div>
                              {loan.notes && (
                                <div className="mt-1 text-xs text-gray-500">
                                  <span className="font-medium">Notes:</span> {loan.notes}
                                </div>
                              )}
                            </div>

                            <div className="mt-4 flex items-center justify-between gap-3">
                              <span className={`inline-flex items-center rounded-full px-2.5 py-1 text-xs font-medium ${statusSurface}`}>
                                <span className={`mr-1.5 h-1.5 w-1.5 rounded-full ${statusAccent}`} />
                                  {nextTask.text}
                              </span>
                              <Link to={`/loan-details/${loan.id}`} className="inline-flex items-center rounded-lg bg-indigo-600 px-3.5 py-2 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700">
                                Open loan <ChevronRightIcon className="ml-1 h-4 w-4" />
                              </Link>
                            </div>
                          </div>
                        </li>
                      );
                    })}
                  </ul>
                </div>
              );
            })}
          </div>

          {/* Floating Action Button */}
          <div className="fixed bottom-6 right-6">
            <div className="flex flex-col space-y-2">
              <button
                onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                className="p-3 bg-indigo-600 text-white rounded-full shadow-lg hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500"
              >
                <svg className="h-6 w-6" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M5 10l7-7m0 0l7 7m-7-7v18" />
                </svg>
              </button>
            </div>
          </div>
        </div>
      );
    } else {
      // Original section-based logic
      if (selectedLoanType === 'calls') {
        const compareDate = new Date(selectedDate);
        
        if (activeSection === 'upcomingCalls') {
          loansToRender = loans.filter(loan => {
            const relevantDate = loan.createdAt?.toDate() || new Date(loan.createdAt);
            return !loan.called && 
                   !loan.allDocumentsUploaded && 
                   isToday(relevantDate, compareDate);
          });
          sectionTitle = "Upcoming Calls";
        } else {
          loansToRender = loans.filter(loan => !loan.allDocumentsUploaded);
          sectionTitle = selectedDate.toLocaleDateString() === new Date().toLocaleDateString()
            ? "Today's Applications"
            : `Applications - ${selectedDate.toLocaleDateString()}`;
        }
      } else if (selectedLoanType === 'workingCapital') {
        switch (activeSection) {
          case LoanStatuses.NEW_APPLICATION:
            loansToRender = loans.filter(loan => loan.loanStatus === LoanStatuses.NEW_APPLICATION);
            sectionTitle = "New Applications";
            break;
          case LoanStatuses.PREQUALIFIED_FOR:
            loansToRender = loans.filter(loan => loan.loanStatus === LoanStatuses.PREQUALIFIED_FOR);
            sectionTitle = "Prequalified";
            break;
          case LoanStatuses.DOCUMENT_CLARIFICATION:
            loansToRender = loans.filter(loan => loan.loanStatus === LoanStatuses.DOCUMENT_CLARIFICATION);
            sectionTitle = "Document Review";
            break;
          case LoanStatuses.UNDERWRITING:
            loansToRender = loans.filter(loan => loan.loanStatus === LoanStatuses.UNDERWRITING);
            sectionTitle = "Underwriting";
            break;
          case LoanStatuses.FUNDED:
            loansToRender = loans.filter(loan => loan.status === LoanStatuses.FUNDED);
            sectionTitle = "Funded";
            break;
          case LoanStatuses.SUBMITTED:
            loansToRender = loans.filter(loan => loan.loanStatus === LoanStatuses.SUBMITTED);
            sectionTitle = "Submitted Applications";
            break;
          case 'bankManagement':
            return <BankManagement />;
          default:
            loansToRender = recentUsers;
            sectionTitle = "Recent Activity";
        }
      } else {
        switch (activeSection) {
          case 'recentActivity':
            loansToRender = recentUsers;
            sectionTitle = "Recent Activity";
            break;
          case 'needsAttention': {
            const today = new Date().toISOString().slice(0, 10);
            loansToRender = loans.filter(loan => {
              const status = String(loan.loanStatus || loan.status || '').toLowerCase();
              const isFinished = ['funded', 'closed', 'rejected', 'complete'].includes(status);
              const followUpDue = loan.followUpDate && String(loan.followUpDate).slice(0, 10) <= today;
              return !isFinished && (!loan.allDocumentsUploaded || followUpDue);
            });
            sectionTitle = "Needs Attention";
            break;
          }
          case 'filesReady':
            loansToRender = loans.filter(loan => {
              const status = String(loan.loanStatus || loan.status || '').toLowerCase();
              return loan.allDocumentsUploaded && !['underwriting', 'in_underwriting', 'funded', 'closed', 'rejected', 'complete'].includes(status);
            });
            sectionTitle = "Files Ready for Review";
            break;
          case 'underwriting':
            loansToRender = loans.filter(loan => ['underwriting', 'in_underwriting', 'in underwriting'].includes(String(loan.loanStatus || loan.status || '').toLowerCase()));
            sectionTitle = "In Underwriting";
            break;
          case 'prequalifications':
            return (
              <div className="px-4 sm:px-6 lg:px-8">
                <h2 className="text-2xl font-semibold mb-6">{sectionTitle}</h2>
                <PrequalificationTabDetails
                  prequalifications={prequalifications}
                  handleCardClick={(application) => {
                    navigate(`/prequalification-details/${application.id}`);
                  }}
                />
              </div>
            );
          case 'bankManagement':
            return <BankManagement />;
          default:
            loansToRender = [];
            sectionTitle = "";
        }
      }
    }

    console.log('Loans to render:', loansToRender);

    if (selectedLoanType === 'lenderMatch') {
      let sectionTitle;
      switch (activeSection) {
        case 'pendingMatches':
          sectionTitle = 'Pending Matches';
          break;
        case 'matched':
          sectionTitle = 'Matched Applications';
          break;
        case 'declined':
          sectionTitle = 'Declined Matches';
          break;
        default:
          sectionTitle = 'All Matches';
      }

      return (
        <div className="px-4 sm:px-6 lg:px-8">
          <div className="flex justify-between items-center mb-6">
            <h2 className="text-2xl font-semibold">{sectionTitle}</h2>
            <button
              onClick={() => setIsCreateModalOpen(true)}
              className="inline-flex items-center px-4 py-2 border border-transparent rounded-md shadow-sm text-sm font-medium text-white bg-indigo-600 hover:bg-indigo-700"
            >
              Create Lender Match
            </button>
          </div>
          
          <div className="grid grid-cols-1 gap-6">
            {lenderMatches.length === 0 ? (
              <p className="text-gray-500">No matches found.</p>
            ) : (
              lenderMatches.map((match) => (
                <LenderMatchCard
                  key={match.id}
                  {...match}
                  onClick={() => navigate(`/lender-match/${match.id}`)}
                />
              ))
            )}
          </div>

          <CreateLenderMatchModal
            isOpen={isCreateModalOpen}
            onClose={() => setIsCreateModalOpen(false)}
            onSuccess={() => {
              // Refresh the lender matches
              const fetchLenderMatches = async () => {
                // ... existing fetchLenderMatches code ...
              };
              fetchLenderMatches();
            }}
          />
        </div>
      );
    }

    return (
      <div className="px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center mb-4">
          <h2 className="text-2xl font-semibold">
            {searchTerm ? `Search Results for "${searchTerm}"` : sectionTitle}
          </h2>
          
          {selectedLoanType !== 'starred' && (
            <div className="flex items-center justify-center space-x-4 bg-white rounded-lg shadow p-2">
              <button
                onClick={() => changeDate('prev')}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
              </button>
              
              <span className="text-lg font-medium px-2">{format(selectedDate, 'MMM d, yyyy')}</span>
              
              <button
                onClick={() => changeDate('next')}
                className="p-2 hover:bg-gray-100 rounded-full"
              >
                <ChevronRightIcon className="h-5 w-5 text-gray-600" />
              </button>
            </div>
          )}

          <div className="flex gap-4">
            <select
              value={selectedLoanType}
              onChange={(e) => setSelectedLoanType(e.target.value)}
              className="block rounded-md border-gray-300 py-1.5 pl-3 pr-10 text-base focus:border-indigo-500 focus:outline-none focus:ring-indigo-500 sm:text-sm"
            >
              {LOAN_TYPE_FILTERS.map((filter) => (
                <option key={filter.value} value={filter.value}>
                  {filter.label}
                </option>
              ))}
            </select>
          </div>
        </div>
        {isSearching ? (
          <div className="flex justify-center items-center py-8">
            <p>Searching...</p>
          </div>
        ) : loansToRender?.length === 0 ? (
          <p>{searchTerm ? 'No results found.' : 'No starred applications found.'}</p>
        ) : (
          <>
            {/* Show notifications only for recent activity */}
            {activeSection === 'recentActivity' && !searchTerm && (
              <NotificationBar
                notifications={notifications}
                onDismiss={dismissNotification}
              />
            )}
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {loansToRender.map((loan) => {
              const displayName = (loan.firstName || loan.lastName) ? `${loan.firstName || ''} ${loan.lastName || ''}`.trim() : loan.businessName;
              return (
                <LightModeLoanCard
                  key={loan.id}
                  id={loan.id}
                  name={displayName}
                  loanSize={loan.projectedSalePrice || loan.loanSize}
                  loanPurpose={loan.businessSummary || loan.loanPurpose}
                  businessState={loan.businessState}
                  businessAge={loan.yearsInBusiness}
                  submittedAt={loan.lastUpdated ? new Date(loan.lastUpdated).toLocaleDateString() : (loan.createdAt && loan.createdAt.toDate ? loan.createdAt.toDate().toLocaleDateString() : 'N/A')}
                  ebitda={loan.ebitda}
                  onClick={() => handleCardClick(loan)}
                  isBlue={activeSection === 'prequalifications'}
                  action={loan.action}
                  firstName={loan.firstName}
                  called={loan.followUpDate ? loan.followupCalled : loan.called}
                  personalGuarantors={loan.personalGuarantors}
                  isFollowUp={!!loan.followUpDate}
                  onCallStatusChange={fetchAllLoans}
                  isCallsTab={selectedLoanType === 'calls'}
                  notes={loan.notes}
                  starred={loan.starred || false}
                  loanStatus={loan.loanStatus}
                  followUpDate={loan.followUpDate}
                  termSheetRequested={loan.businesses?.some(b => b.termSheetRequested) || loan.termSheetRequested || false}
                  allDocumentsUploaded={loan.allDocumentsUploaded || false}
                  prequalified={loan.businesses?.some(b => b.prequalified) || loan.prequalified || loan.loanStatus === LoanStatuses.PREQUALIFIED_FOR || false}
                  hasChosenListing={loan.hasChosenListing || loan.businesses?.some(b => b.chosenAsBuyer) || false}
                  chosenListingName={loan.businesses?.find(b => b.chosenAsBuyer)?.listingName || ''}
                />
              );
            })}
          </div>
          </>
        )}
      </div>
    );
  };

  const handleSaveFinancials = async (loanId, year, financials) => {
    const loanRef = doc(db, 'loanApplications', loanId);
    
    try {
      await updateDoc(loanRef, {
        [`financialAnalysis.${year}`]: financials
      });
      console.log('Financials saved successfully');
    } catch (error) {
      console.error('Error saving financials:', error);
      throw error;
    }
  };

  useEffect(() => {
    const fetchLenderMatches = async () => {
      if (selectedLoanType === 'lenderMatch') {
        try {
          const matchesRef = collection(db, 'lenderMatches');
          let q;
          
          // Filter based on active section
          switch (activeSection) {
            case 'pendingMatches':
              q = query(matchesRef, where('status', '==', 'pending'));
              break;
            case 'matched':
              q = query(matchesRef, where('status', '==', 'matched'));
              break;
            case 'declined':
              q = query(matchesRef, where('status', '==', 'declined'));
              break;
            default:
              q = query(matchesRef);
          }

          const querySnapshot = await getDocs(q);
          const matchesData = querySnapshot.docs.map(doc => ({
            id: doc.id,
            ...doc.data()
          }));
          setLenderMatches(matchesData);
        } catch (error) {
          console.error('Error fetching lender matches:', error);
          setLenderMatches([]);
        }
      }
    };

    fetchLenderMatches();
  }, [selectedLoanType, activeSection]);

  // Generate notifications when loan data changes
  useEffect(() => {
    const fetchAllNotifications = async () => {
      if (activeSection === 'recentActivity' && loans.length > 0) {
        const allLoans = [...recentUsers, ...loans];
        const loanNotifications = generateNotifications(allLoans);
        const documentNotifications = await fetchRecentDocumentUploads();
        const standAloneFollowUpNotifications = await fetchStandAloneFollowUps();

        const allNotifications = [...loanNotifications, ...documentNotifications, ...standAloneFollowUpNotifications];
        const priorityOrder = { high: 0, medium: 1, low: 2 };
        allNotifications.sort((a, b) => {
          if (priorityOrder[a.priority] !== priorityOrder[b.priority]) {
            return priorityOrder[a.priority] - priorityOrder[b.priority];
          }
          return b.timestamp - a.timestamp;
        });
        setNotifications(allNotifications);
      }
    };

    fetchAllNotifications();
  }, [loans, recentUsers, activeSection, followUpRefreshTrigger]);

  // Clean up old dismissed notifications (older than 7 days)
  useEffect(() => {
    const cleanupDismissedNotifications = () => {
      const saved = localStorage.getItem('dismissedNotifications');
      if (saved) {
        try {
          const dismissedArray = JSON.parse(saved);
          const sevenDaysAgo = Date.now() - (7 * 24 * 60 * 60 * 1000);
          
          // Filter out notification IDs that contain timestamps older than 7 days
          const filteredDismissed = dismissedArray.filter(id => {
            // Extract timestamp from notification ID if it contains one
            const match = id.match(/(\d{13})/); // 13 digits for timestamp
            if (match) {
              const timestamp = parseInt(match[1]);
              return timestamp > sevenDaysAgo;
            }
            return true; // Keep IDs that don't have timestamps
          });
          
          if (filteredDismissed.length !== dismissedArray.length) {
            localStorage.setItem('dismissedNotifications', JSON.stringify(filteredDismissed));
            setDismissedNotifications(new Set(filteredDismissed));
          }
        } catch (error) {
          console.error('Error cleaning up dismissed notifications:', error);
        }
      }
    };

    // Run cleanup on component mount and then daily
    cleanupDismissedNotifications();
    const intervalId = setInterval(cleanupDismissedNotifications, 24 * 60 * 60 * 1000);
    
    return () => clearInterval(intervalId);
  }, []);

  const dismissNotification = (notificationId) => {
    setDismissedNotifications(prev => {
      const newDismissed = new Set(prev).add(notificationId);
      // Save dismissed notifications to localStorage
      localStorage.setItem('dismissedNotifications', JSON.stringify(Array.from(newDismissed)));
      return newDismissed;
    });
  };

  const NotificationBar = ({ notifications, onDismiss }) => {
    const visibleNotifications = notifications.filter(notification => 
      !dismissedNotifications.has(notification.id)
    );

    if (!visibleNotifications || visibleNotifications.length === 0) return null;

    const getNotificationIcon = (type) => {
      switch (type) {
        case 'followup_today':
          return <ClockIcon className="h-5 w-5" />;
        case 'new_application':
          return <UserPlusIcon className="h-5 w-5" />;
        case 'document_upload':
          return <DocumentTextIcon className="h-5 w-5" />;
        case 'standalone_followup':
          return <PhoneIcon className="h-5 w-5" />;
        default:
          return <BellIcon className="h-5 w-5" />;
      }
    };

    const getNotificationColors = (priority) => {
      switch (priority) {
        case 'high':
          return 'bg-purple-50 border-purple-200 text-purple-800';
        case 'medium':
          return 'bg-blue-50 border-blue-200 text-blue-800';
        case 'low':
          return 'bg-gray-50 border-gray-200 text-gray-800';
        default:
          return 'bg-gray-50 border-gray-200 text-gray-800';
      }
    };

    const handleNotificationClick = (notification) => {
      if (notification.loanId) {
        // Navigate to loan details for loan-related notifications
        navigate(`/loan-details/${notification.loanId}`);
      } else if (notification.standAloneFollowUpId) {
        // Handle standalone follow-up click - could open a modal or navigate to a follow-up page
        alert(`Follow-up: ${notification.followUpData?.personName}\nReason: ${notification.followUpData?.reason}\nEmail: ${notification.followUpData?.email || 'N/A'}\nPhone: ${notification.followUpData?.phoneNumber || 'N/A'}`);
      }
    };

    return (
      <div className="space-y-3 mb-6">
        {visibleNotifications.map((notification) => (
          <div
            key={notification.id}
            className={`rounded-lg border-2 p-4 shadow-sm ${getNotificationColors(notification.priority)}`}
          >
            <div className="flex items-start">
              <div className={`flex-shrink-0 p-1 rounded-full ${
                notification.priority === 'high' ? 'bg-purple-100' :
                notification.priority === 'medium' ? 'bg-blue-100' : 'bg-gray-100'
              }`}>
                {getNotificationIcon(notification.type)}
              </div>
              <div className="ml-3 flex-1 min-w-0">
                <p className="text-sm font-medium">{notification.title}</p>
                <p className="text-sm opacity-90">{notification.message}</p>
                {(notification.loanId || notification.standAloneFollowUpId) && (
                  <button
                    onClick={() => handleNotificationClick(notification)}
                    className="mt-2 text-xs underline hover:no-underline"
                  >
                    {notification.standAloneFollowUpId ? 'View Follow-up Details' : 'View Application'}
                  </button>
                )}
              </div>
              <div className="ml-4 flex-shrink-0">
                <button
                  onClick={() => onDismiss(notification.id)}
                  className="rounded-md p-1.5 hover:bg-gray-200 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-400"
                >
                  <XMarkIcon className="h-4 w-4" aria-hidden="true" />
                </button>
              </div>
            </div>
          </div>
        ))}
      </div>
    );
  };

  // Handle new follow-up creation
  const handleCreateFollowUp = async () => {
    const { personName, email, phoneNumber, followUpDate, reason } = newFollowUpData;
    
    if (!personName || !followUpDate || !reason) {
      alert('Please fill in at least the person name, follow-up date, and reason.');
      return;
    }

    try {
      // Create a new follow-up document in Firestore
      const followUpRef = collection(db, 'standAloneFollowUps');
      await addDoc(followUpRef, {
        personName,
        email: email || null,
        phoneNumber: phoneNumber || null,
        followUpDate,
        reason,
        createdAt: serverTimestamp(),
        completed: false
      });

      // Reset form and close modal
      setNewFollowUpData({
        personName: '',
        email: '',
        phoneNumber: '',
        followUpDate: '',
        reason: ''
      });
      setIsNewFollowUpModalOpen(false);
      alert('Follow-up created successfully!');

      // Trigger a refresh of notifications
      setFollowUpRefreshTrigger(prev => prev + 1);
    } catch (error) {
      console.error('Error creating follow-up:', error);
      alert('Error creating follow-up. Please try again.');
    }
  };

  // Fetch off-market searchers
  const fetchOffMarketSearchers = async () => {
    try {
      const offMarketRef = collection(db, 'offMarketSearchers');
      const q = query(offMarketRef, where('status', '!=', 'closed'), orderBy('status'), orderBy('createdAt', 'desc'));
      const snapshot = await getDocs(q);

      const searchers = snapshot.docs.map(doc => ({
        id: doc.id,
        ...doc.data()
      }));

      setOffMarketSearchers(searchers);
    } catch (error) {
      console.error('Error fetching off-market searchers:', error);
    }
  };

  // Handle marking off-market searcher as responded
  const handleMarkResponded = async (searcherId) => {
    try {
      const searcherRef = doc(db, 'offMarketSearchers', searcherId);
      await updateDoc(searcherRef, {
        responded: true,
        respondedAt: serverTimestamp(),
        status: 'responded'
      });
      fetchOffMarketSearchers();
    } catch (error) {
      console.error('Error marking as responded:', error);
      alert('Error updating status. Please try again.');
    }
  };

  // Handle sending follow-up email to off-market searcher
  const handleOffMarketFollowUp = (searcher) => {
    const firstName = searcher.name?.split(' ')[0] || searcher.name;
    setEmailModalData({
      recipientEmail: searcher.email,
      recipientName: firstName,
      templateType: 'offMarketFollowUp',
      businessName: ''
    });
    setShowEmailModal(true);
  };

  // Handle closing off-market searcher
  const handleCloseOffMarket = async (searcherId) => {
    if (!window.confirm('Are you sure you want to close this off-market searcher?')) return;
    try {
      const searcherRef = doc(db, 'offMarketSearchers', searcherId);
      await updateDoc(searcherRef, {
        status: 'closed'
      });
      fetchOffMarketSearchers();
    } catch (error) {
      console.error('Error closing off-market searcher:', error);
      alert('Error closing. Please try again.');
    }
  };

  // Fetch standalone follow-ups for notifications
  const fetchStandAloneFollowUps = async () => {
    try {
      const followUpsRef = collection(db, 'standAloneFollowUps');
      const today = new Date().toISOString().split('T')[0];
      
      // Get all uncompleted follow-ups first, then filter by date
      const q = query(
        followUpsRef,
        where('completed', '==', false)
      );
      
      const querySnapshot = await getDocs(q);
      const followUpNotifications = [];
      
      querySnapshot.forEach((doc) => {
        const data = doc.data();
        const followUpDate = data.followUpDate;
        
        // Only include follow-ups that are due today or overdue
        if (followUpDate && followUpDate <= today) {
          const isPastDue = followUpDate < today;
          const isDueToday = followUpDate === today;
          
          followUpNotifications.push({
            id: `standalone-followup-${doc.id}`,
            type: 'standalone_followup',
            title: isPastDue ? 'Overdue Follow-up' : 'Follow-up Due Today',
            message: `${data.personName} - ${data.reason}`,
            standAloneFollowUpId: doc.id,
            priority: (isPastDue || isDueToday) ? 'high' : 'medium',
            timestamp: data.createdAt?.toDate() || new Date(),
            followUpData: data
          });
        }
      });
      
      console.log(`Found ${followUpNotifications.length} standalone follow-up notifications for today/overdue`);
      return followUpNotifications;
    } catch (error) {
      console.error('Error fetching standalone follow-ups:', error);
      return [];
    }
  };

  const adminSections = [
    ['adminHome', 'Home'],
    ['Acquisition', 'Full Deals'],
    ['prequalification', 'Prequalifications'],
    ['brokers', 'Brokers'],
    ['commissions', 'Sales Reps & Commissions'],
    ['banks', 'Banks'],
    ['dailyTodo', 'Daily To-Do'],
  ];

  const selectedSectionLabel = adminSections.find(([value]) => value === selectedLoanType)?.[1] || 'Dashboard';

  return (
    <div className="min-h-screen bg-gray-50">
      <header className="sticky top-0 z-40 border-b border-gray-200 bg-white/95 backdrop-blur">
        <div className="max-w-screen-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="flex min-h-20 flex-col gap-4 py-4 lg:flex-row lg:items-center lg:justify-between">
            <div>
              <div className="flex items-center gap-3">
                <div className="flex h-10 w-10 items-center justify-center rounded-xl bg-indigo-600 text-sm font-bold text-white shadow-sm">L</div>
                <div>
                  <h1 className="text-xl font-bold tracking-tight text-gray-900">Lended Admin</h1>
                  <p className="text-sm text-gray-500">Manage applications, borrowers, and follow-ups</p>
                </div>
              </div>
            </div>
            <div className="flex flex-wrap items-center gap-2">
              <button onClick={() => setShowNewListingLauncher(true)} className="inline-flex items-center gap-1.5 rounded-xl bg-indigo-600 px-4 py-2.5 text-sm font-semibold text-white shadow-sm hover:bg-indigo-700">
                <PlusIcon className="h-4 w-4" /> New Listing
              </button>
              <button onClick={() => setShowUploadPrequalModal(true)} className="inline-flex items-center gap-1.5 rounded-xl border border-blue-200 bg-blue-50 px-3.5 py-2.5 text-sm font-semibold text-blue-700 shadow-sm hover:bg-blue-100">
                <DocumentTextIcon className="h-4 w-4" /> Upload Prequal
              </button>
              <button onClick={() => setIsNewFollowUpModalOpen(true)} className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3.5 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50">
                <ClockIcon className="h-4 w-4 text-blue-500" /> Follow Up
              </button>
              <button onClick={() => setShowOffMarketModal(true)} className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3.5 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50">
                <GlobeAltIcon className="h-4 w-4 text-teal-500" /> Off-Market
              </button>
              <button onClick={() => setShowPendingSearcherModal(true)} className="inline-flex items-center gap-1.5 rounded-xl border border-gray-300 bg-white px-3.5 py-2.5 text-sm font-medium text-gray-700 shadow-sm hover:bg-gray-50">
                <UserPlusIcon className="h-4 w-4 text-amber-500" /> Searcher
              </button>
              <button onClick={handleLogout} title="Logout" className="inline-flex h-10 w-10 items-center justify-center rounded-xl border border-gray-300 bg-white text-gray-500 shadow-sm hover:bg-gray-50 hover:text-red-600">
                <ArrowRightOnRectangleIcon className="h-5 w-5" />
              </button>
            </div>
          </div>

          <div className="flex gap-1 overflow-x-auto border-t border-gray-100 py-3">
            {adminSections.map(([value, label]) => (
              <button
                key={value}
                type="button"
                onClick={() => handleLoanTypeChange(value)}
                className={`whitespace-nowrap rounded-lg px-4 py-2 text-sm font-medium transition-colors ${selectedLoanType === value ? 'bg-indigo-50 text-indigo-700' : 'text-gray-500 hover:bg-gray-50 hover:text-gray-900'}`}
              >
                {label}
              </button>
            ))}
          </div>

          {/* Show tabs only for specific views */}
          {selectedLoanType === 'Acquisition' && (
            <div className="flex flex-col gap-3 border-t border-gray-100 py-3 sm:flex-row sm:items-center sm:justify-between">
              <h2 className="text-sm font-semibold text-gray-900">{selectedSectionLabel}</h2>
              <div>
                <div className="hidden sm:block">
                  <nav className="flex gap-1 rounded-xl bg-gray-100 p-1">
                    {getCurrentTabs().map((tab) => (
                      <button
                        key={tab.name}
                        onClick={() => setActiveSection(tab.section)}
                        className={classNames(
                          activeSection === tab.section
                            ? 'bg-white text-indigo-700 shadow-sm'
                            : 'text-gray-500 hover:text-gray-900',
                          'whitespace-nowrap rounded-lg px-3 py-1.5 text-sm font-medium'
                        )}
                      >
                        {tab.name}
                      </button>
                    ))}
                  </nav>
                </div>
              </div>
            </div>
          )}
          
          {selectedLoanType !== 'dailyTodo' && selectedLoanType !== 'brokers' && selectedLoanType !== 'commissions' && <div className="border-t border-gray-100 py-3">
            <div className="relative max-w-xl">
              <MagnifyingGlassIcon className="pointer-events-none absolute left-3 top-1/2 h-5 w-5 -translate-y-1/2 text-gray-400" aria-hidden="true" />
              <input
                type="text"
                name="search"
                id="search"
                className="block w-full rounded-xl border border-gray-300 bg-gray-50 py-2.5 pl-10 pr-4 text-sm text-gray-900 shadow-sm placeholder:text-gray-400 focus:border-indigo-500 focus:bg-white focus:ring-1 focus:ring-indigo-500"
                placeholder="Search applicants, businesses, or email..."
                value={searchTerm}
                onChange={handleSearch}
              />
            </div>
          </div>}
          </div>
      </header>
      <main>
        <div className="max-w-screen-2xl mx-auto px-4 py-8 sm:px-6 lg:px-8">
          <div>
            {selectedLoanType === 'commissions' ? (
              <SalesCommissionHub canManageReps />
            ) : selectedLoanType === 'brokers' ? (
              <BrokersTab />
            ) : selectedLoanType === 'adminHome' ? (
              <AdminOverview
                loans={loans}
                prequalifications={prequalifications}
                borrowers={borrowerPrequalifications}
                onNavigate={handleLoanTypeChange}
                onNewListing={() => setShowNewListingLauncher(true)}
                onUploadPrequal={() => setShowUploadPrequalModal(true)}
              />
            ) : selectedLoanType === 'banks' ? (
              <BankManagement />
            ) : selectedLoanType === 'dailyTodo' ? (
              <DailyTodo />
            ) : selectedLoanType === 'prequalification' ? (
              <PrequalificationTabDetails
                prequalifications={prequalifications}
                borrowers={borrowerPrequalifications}
                handleCardClick={(application) => {
                  navigate(`/prequalification-details/${application.id}`);
                }}
              />
            ) : (
              renderSection()
            )}
          </div>
        </div>
      </main>

      <UploadBrokerPrequalificationModal
        open={showUploadPrequalModal}
        onClose={() => setShowUploadPrequalModal(false)}
        onUploaded={(record) => {
          setPrequalifications((current) => [{ ...record, status: 'prequalified', source: 'admin_upload' }, ...current]);
          handleLoanTypeChange('prequalification');
        }}
      />
      <NewListingLauncherModal
        open={showNewListingLauncher}
        onClose={() => setShowNewListingLauncher(false)}
        onContinue={(brokerId) => navigate(`/admin/new-listing?type=broker&id=${brokerId}`)}
      />

      {/* Off-Market Searchers Section */}
      {offMarketSearchers.length > 0 && (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-6">
          <div className="bg-white shadow rounded-lg overflow-hidden">
            <div className="px-6 py-4 border-b border-gray-200 bg-teal-50">
              <div className="flex items-center justify-between">
                <div className="flex items-center">
                  <GlobeAltIcon className="h-6 w-6 text-teal-600 mr-3" />
                  <h3 className="text-lg font-semibold text-gray-900">Off-Market Searchers</h3>
                  <span className="ml-3 bg-teal-100 text-teal-800 py-0.5 px-2.5 rounded-full text-xs font-medium">
                    {offMarketSearchers.length}
                  </span>
                </div>
              </div>
            </div>
            <div className="p-6">
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {offMarketSearchers.map((searcher) => (
                  <div
                    key={searcher.id}
                    className={`border rounded-lg p-4 ${
                      searcher.status === 'responded'
                        ? 'border-green-200 bg-green-50'
                        : searcher.status === 'awaiting_response'
                        ? 'border-teal-200 bg-teal-50'
                        : 'border-gray-200 bg-gray-50'
                    }`}
                  >
                    <div className="flex items-start justify-between">
                      <div>
                        <p className="font-medium text-gray-900">{searcher.name}</p>
                        <p className="text-xs text-gray-500">{searcher.email}</p>
                        {searcher.phoneNumber && (
                          <p className="text-xs text-gray-500">{searcher.phoneNumber}</p>
                        )}
                      </div>
                      <span className={`text-xs px-2 py-0.5 rounded-full ${
                        searcher.status === 'responded'
                          ? 'bg-green-100 text-green-700'
                          : searcher.status === 'awaiting_response'
                          ? 'bg-teal-100 text-teal-700'
                          : 'bg-gray-100 text-gray-700'
                      }`}>
                        {searcher.status === 'responded' ? 'Responded' :
                         searcher.status === 'awaiting_response' ? 'Awaiting' : 'Pending'}
                      </span>
                    </div>
                    {searcher.notes && (
                      <p className="text-xs text-gray-600 mt-2">{searcher.notes}</p>
                    )}
                    <div className="mt-3 flex gap-2">
                      {searcher.status !== 'responded' && (
                        <>
                          <button
                            onClick={() => handleOffMarketFollowUp(searcher)}
                            className="flex-1 text-xs px-2 py-1 bg-teal-100 text-teal-700 rounded hover:bg-teal-200"
                          >
                            Follow Up
                          </button>
                          <button
                            onClick={() => handleMarkResponded(searcher.id)}
                            className="flex-1 text-xs px-2 py-1 bg-green-100 text-green-700 rounded hover:bg-green-200"
                          >
                            Mark Responded
                          </button>
                        </>
                      )}
                      <button
                        onClick={() => handleCloseOffMarket(searcher.id)}
                        className="text-xs px-2 py-1 bg-gray-100 text-gray-600 rounded hover:bg-gray-200"
                      >
                        Close
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* End of main content */}

      {/* New Follow Up Modal */}
      {isNewFollowUpModalOpen && (
        <div className="fixed z-10 inset-0 overflow-y-auto" aria-labelledby="modal-title" role="dialog" aria-modal="true">
          <div className="flex items-end justify-center min-h-screen pt-4 px-4 pb-20 text-center sm:block sm:p-0">
            {/* Background overlay */}
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" aria-hidden="true"></div>
            {/* Modal panel */}
            <span className="hidden sm:inline-block sm:align-middle sm:h-screen" aria-hidden="true">&#8203;</span>
            <div className="inline-block align-bottom bg-white rounded-lg text-left overflow-hidden shadow-xl transform transition-all sm:my-8 sm:align-middle sm:max-w-lg sm:w-full">
              <div className="bg-white px-4 pt-5 pb-4 sm:p-6 sm:pb-4">
                <div className="sm:flex sm:items-start">
                  <div className="mt-3 text-center sm:mt-0 sm:text-left w-full">
                    <h3 className="text-lg leading-6 font-medium text-gray-900" id="modal-title">
                      Create New Follow Up
                    </h3>
                    <div className="mt-4 space-y-4">
                      <div>
                        <label htmlFor="person-name" className="block text-sm font-medium text-gray-700">
                          Person Name *
                        </label>
                        <input
                          type="text"
                          id="person-name"
                          value={newFollowUpData.personName}
                          onChange={(e) => setNewFollowUpData({...newFollowUpData, personName: e.target.value})}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          placeholder="Enter person's name"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="email" className="block text-sm font-medium text-gray-700">
                          Email
                        </label>
                        <input
                          type="email"
                          id="email"
                          value={newFollowUpData.email}
                          onChange={(e) => setNewFollowUpData({...newFollowUpData, email: e.target.value})}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          placeholder="Enter email address"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="phone-number" className="block text-sm font-medium text-gray-700">
                          Phone Number
                        </label>
                        <input
                          type="tel"
                          id="phone-number"
                          value={newFollowUpData.phoneNumber}
                          onChange={(e) => setNewFollowUpData({...newFollowUpData, phoneNumber: e.target.value})}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          placeholder="Enter phone number"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="follow-up-date" className="block text-sm font-medium text-gray-700">
                          Follow-up Date *
                        </label>
                        <input
                          type="date"
                          id="follow-up-date"
                          value={newFollowUpData.followUpDate}
                          onChange={(e) => setNewFollowUpData({...newFollowUpData, followUpDate: e.target.value})}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                        />
                      </div>
                      
                      <div>
                        <label htmlFor="reason" className="block text-sm font-medium text-gray-700">
                          Reason *
                        </label>
                        <textarea
                          id="reason"
                          rows={3}
                          value={newFollowUpData.reason}
                          onChange={(e) => setNewFollowUpData({...newFollowUpData, reason: e.target.value})}
                          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-indigo-500 focus:ring-indigo-500 sm:text-sm"
                          placeholder="Enter the reason for this follow-up"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              </div>
              <div className="bg-gray-50 px-4 py-3 sm:px-6 sm:flex sm:flex-row-reverse">
                <button
                  type="button"
                  className="w-full inline-flex justify-center rounded-md border border-transparent shadow-sm px-4 py-2 bg-blue-600 text-base font-medium text-white hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={handleCreateFollowUp}
                >
                  Create Follow Up
                </button>
                <button
                  type="button"
                  className="mt-3 w-full inline-flex justify-center rounded-md border border-gray-300 shadow-sm px-4 py-2 bg-white text-base font-medium text-gray-700 hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-indigo-500 sm:mt-0 sm:ml-3 sm:w-auto sm:text-sm"
                  onClick={() => {
                    setIsNewFollowUpModalOpen(false);
                    setNewFollowUpData({
                      personName: '',
                      email: '',
                      phoneNumber: '',
                      followUpDate: '',
                      reason: ''
                    });
                  }}
                >
                  Cancel
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Off-Market Modal */}
      <OffMarketModal
        isOpen={showOffMarketModal}
        onClose={() => setShowOffMarketModal(false)}
        onSuccess={fetchOffMarketSearchers}
      />

      {/* Pending Searcher Modal */}
      <PendingSearcherModal
        isOpen={showPendingSearcherModal}
        onClose={() => setShowPendingSearcherModal(false)}
      />

      {/* Follow-up Email Modal */}
      <FollowUpEmailModal
        isOpen={showEmailModal}
        onClose={() => setShowEmailModal(false)}
        recipientEmail={emailModalData.recipientEmail}
        recipientName={emailModalData.recipientName}
        templateType={emailModalData.templateType}
        businessName={emailModalData.businessName}
      />
    </div>
  );
};

export default AdminDashboard;
